export type WsHandler = (data?: any) => void;

export type RegisterWsHandlerOptions = {
  /**
   * Collapse a burst of same-type messages arriving within this many ms into
   * a single handler call using the most recent payload — e.g. one backend
   * request that fans out several NotificationQueueHelper::publish() calls
   * (one per affected row) otherwise triggers one REST re-fetch + re-render
   * per message. Window starts on the first message of a burst and does not
   * reset on each new one, so a sustained stream still flushes every
   * `coalesceMs` instead of being starved indefinitely.
   *
   * Only safe when the payload is a full-snapshot or signal-only (the
   * listener re-fetches on receipt) — an intermediate message's data is
   * discarded, never merged. Never set this for a type whose payload is a
   * per-message delta meant to be applied individually (SMS_THREAD_UPDATED,
   * GMAIL_THREAD_UPDATED — see call-board.ts/gmail.ts) or a discrete item
   * that must not be dropped (CSR_PROMPT_ISSUED/RESOLVED) — coalescing those
   * silently loses messages instead of just delaying a re-fetch.
   */
  coalesceMs?: number;
};

const handlers = new Map<string, Set<WsHandler>>();
const coalesceWindows = new Map<string, number>();
const pendingTimers = new Map<string, ReturnType<typeof setTimeout>>();
const pendingData = new Map<string, any>();

/**
 * Lets a feature react to one WS message `type` without transport.ts having
 * to import that feature — call this once at module load, right next to
 * that feature's own emit()/on() pair (see notification.ts, csr-activity.ts,
 * csrProductivity.ts for the existing pattern this plugs into).
 */
export function registerWsHandler(type: string, handler: WsHandler, options?: RegisterWsHandlerOptions): void {
  if (!handlers.has(type)) {
    handlers.set(type, new Set());
  }
  handlers.get(type)!.add(handler);
  if (options?.coalesceMs) {
    coalesceWindows.set(type, options.coalesceMs);
  }
}

function flush(type: string): void {
  pendingTimers.delete(type);
  const data = pendingData.get(type);
  pendingData.delete(type);
  handlers.get(type)?.forEach((handler) => handler(data));
}

/** transport.ts calls this for every parsed WS message. */
export function dispatchWsMessage(msg: { type?: string; data?: any }): void {
  const type = msg?.type;
  if (!type) return;

  const coalesceMs = coalesceWindows.get(type);
  if (!coalesceMs) {
    handlers.get(type)?.forEach((handler) => handler(msg.data));
    return;
  }

  pendingData.set(type, msg.data);
  if (!pendingTimers.has(type)) {
    pendingTimers.set(type, setTimeout(() => flush(type), coalesceMs));
  }
}
